import { useEffect, useState } from "react";

/**
 * Hook to detect if a media query matches.
 * Returns undefined during SSR/hydration to prevent flash.
 * Handles environments where window.matchMedia is not available (e.g., jsdom tests).
 *
 * @param query - The media query string (e.g., "(min-width: 768px)")
 * @returns boolean | undefined - undefined during hydration, then true/false
 */
export function useMediaQuery(query: string): boolean | undefined {
  const [matches, setMatches] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      setMatches(false);
      return;
    }

    const media = window.matchMedia(query);
    setMatches(media.matches);

    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}
